import React, { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CrudService from "services/cruds-service";
import ClusteredGatewayMarker from "examples/Maps/components/ClusteredGatewayMarker";
import { Map } from "@vis.gl/react-google-maps";
import { useWebSocket } from "context/WebSocketContext";
import { normalizeDashboardRows } from "./utils";
import { TANK_FIELDS, TEMP_FIELDS, dashboardCardSx, dashboardInputSx } from "./constants";
import DashboardSummaryCard from "./components/DashboardSummaryCard";
import DeviceDashboardTable from "./components/DeviceDashboardTable";
import ThresholdDialog from "./components/ThresholdDialog";
import AlertDialog from "./components/AlertDialog";

function getSortValue(row, key) {
  if (TANK_FIELDS.includes(key)) return row[key]?.numericPercent ?? -Infinity;
  if (TEMP_FIELDS.includes(key)) return row[key]?.numericValue ?? -Infinity;
  if (key === "alert") return row.alert === "-" ? 0 : 1;
  if (key === "batteryVoltage") return row.batteryNumeric ?? -Infinity;
  return String(row[key] || "").toLowerCase();
}

const GoogleMapBoardComponent = React.memo(
  ({ devices, deviceType, selectedRow, onSelectRow, resetSelectedeDevice }) => {
    const MarkerComponent = useMemo(() => {
      const commonProps = {
        devices,
        deviceType: 9,
        selectedRow,
        onSelectRow,
        resetSelectedeDevice,
      };

      let component = null;

      switch (deviceType) {
        case 9:
          component = <ClusteredGatewayMarker {...commonProps} />
          break;
        default:
          component = ''
          break;
      }

      return component;

    }, [devices, deviceType, selectedRow, onSelectRow, resetSelectedeDevice]);

    return (
      <MDBox style={{ width: "100%", height: "600px" }}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={{ lat: 0, lng: 0 }}
          defaultZoom={2}
          gestureHandling="greedy"
          mapId="d089c1a7f8831a36"
        >
          {MarkerComponent}
        </Map>
      </MDBox>
    );
  },
  // custom comparison function to prevent re-rendering if props are the same
  (prevProps, nextProps) =>
    prevProps.deviceType === nextProps.deviceType &&
    prevProps.selectedRow === nextProps.selectedRow &&
    prevProps.onSelectRow === nextProps.onSelectRow &&
    prevProps.resetSelectedeDevice === nextProps.resetSelectedeDevice &&
    JSON.stringify(prevProps.devices) === JSON.stringify(nextProps.devices)
);

function Dashboard2() {
  const [devices, setDevices] = useState([]);
  const [badgesList, setBadgeList] = useState([])
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState({ key: "name", direction: "asc" });
  const [thresholdRow, setThresholdRow] = useState(null);
  const [alertRow, setAlertRow] = useState(null);
  // WebSocket data for gateway devices
  const { holyKelData } = useWebSocket();

  // Normalize devices into rows and filter out any null entries (e.g., devices without location data)
  const rows = useMemo(() => normalizeDashboardRows(devices, badgesList).filter(Boolean), [devices]);

  const visibleRows = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    const filtered = search
      ? rows.filter((row) =>
        [row.name, row.status, row.power, row.location, row.identifier, row.alert, row.lastUpdated, row.entityName, row.itemName]
          .join(" ")
          .toLowerCase()
          .includes(search)
      )
      : rows;

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a, sortState.key);
      const bValue = getSortValue(b, sortState.key);
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortState.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortState.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rows, searchQuery, sortState]);

  const visibleRowsForMap = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    const filtered = search
      ? rows.filter((row) =>
        [row.name, row.status, row.power, row.location, row.identifier, row.alert, row.lastUpdated, row.entityName, row.itemName]
          .join(" ")
          .toLowerCase()
          .includes(search)
      )
      : rows;

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a, sortState.key);
      const bValue = getSortValue(b, sortState.key);
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortState.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortState.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [loading, searchQuery, sortState]);

  const summaryCards = useMemo(
    () => [
      { label: "Main devices", value: visibleRows.length },
      { label: "Fresh tanks", value: visibleRows.filter((row) => row.fresh.percent !== "-").length },
      {
        label: "Waste tanks",
        value: visibleRows.reduce(
          (count, row) => count + ["waste", "waste2", "waste3"].filter((key) => row[key].percent !== "-").length,
          0
        ),
      },
      { label: "Alerts", value: visibleRows.filter((row) => row.alert !== "-").length },
    ],
    [visibleRows]
  );

  function handleSort(key) {
    setSortState((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  useEffect(() => {
    document.title = "Rivio | Dashboard 2";
    (async () => {
      try {
        let badges = await CrudService.getBadges();
        if (badges?.data) {
          setBadgeList(badges?.data)
        }
      } catch (error) {
        console.error('badges error', error)
      }
      try {
        const response = await CrudService.getInternalMapDevices(9);
        if (response?.data?.length) {
          setDevices(response.data);
          return;
        }

        const fallbackResponse = await CrudService.getInternalMapDevices(4);
        setDevices(fallbackResponse?.data || []);
      } catch (error) {
        console.error("Dashboard 2 devices error", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When gateway socket data arrives, refetch devices to get latest state
  useEffect(() => {
    if (!holyKelData) return;
    // console.warn('check gatwway', holyKelData);
    if (holyKelData?.type !== 'duplicate') {
      const { data, linked_devices, device_id } = holyKelData

      setDevices((prevDevices) => {
        // Update the devices array with the new data
        return prevDevices.map((device) => {
          if (device.id === device_id) {
            let payload = {
              ...device,
              attributes: {
                ...device.attributes,
                device_value: data,
                ...linked_devices
              }
            }
            return { ...device, ...payload };
          }
          return device;
        });
      });
    }
  }, [holyKelData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <GoogleMapBoardComponent
          devices={visibleRowsForMap}
          onSelectRow={() => { }}
          style={{ height: '500px' }} // Adjust the height here
          deviceType={9}
        />

        <Card sx={{ ...dashboardCardSx, mb: 2 }}>
          <MDBox p={1.5} display="flex" gap={1.5} alignItems="center">
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              placeholder="Search device, location, status, or identifier..."
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={dashboardInputSx}
            />
            {loading && <CircularProgress size={24} />}
          </MDBox>
        </Card>

        <Grid container spacing={2} mb={2}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.label}>
              <DashboardSummaryCard label={card.label} value={card.value} />
            </Grid>
          ))}
        </Grid>

        <DeviceDashboardTable
          rows={visibleRows}
          sortState={sortState}
          onSort={handleSort}
          onRowClick={setThresholdRow}
          onAlertClick={setAlertRow}
        />
      </MDBox>
      <ThresholdDialog row={thresholdRow} onClose={() => setThresholdRow(null)} />
      <AlertDialog row={alertRow} onClose={() => setAlertRow(null)} />
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard2;
